import { jsPDF } from 'jspdf';

const bucketRows = [
  ['current', 'Current'],
  ['1-30', '1–30 Days'],
  ['31-60', '31–60 Days'],
  ['61-90', '61–90 Days'],
  ['91+', '91+ Days']
];

const formatAmount = (value) => Number(value || 0).toFixed(2);

const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const buildRows = (report, viewMode) => {
  if (viewMode === 'summary') {
    return [
      ['Aging Bucket', 'Amount'],
      ...bucketRows.map(([key, label]) => [
        label,
        formatAmount(report.grandTotals.buckets[key])
      ]),
      ['Total Receivables', formatAmount(report.grandTotals.total)]
    ];
  }

  return [
    ['Customer', 'Current', '1–30 Days', '31–60 Days', '61–90 Days', '91+ Days', 'Total'],
    ...report.customers.map((customer) => [
      customer.name,
      formatAmount(customer.buckets.current),
      formatAmount(customer.buckets['1-30']),
      formatAmount(customer.buckets['31-60']),
      formatAmount(customer.buckets['61-90']),
      formatAmount(customer.buckets['91+']),
      formatAmount(customer.total)
    ]),
    [
      'Grand Total',
      ...bucketRows.map(([key]) => formatAmount(report.grandTotals.buckets[key])),
      formatAmount(report.grandTotals.total)
    ]
  ];
};

export const downloadARAgingCsv = ({ report, viewMode, asOfDate }) => {
  const rows = buildRows(report, viewMode);
  const content = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(',')).join('\r\n')}`;

  downloadBlob(
    content,
    `Accounts_Receivable_Aging_${asOfDate}.csv`,
    'text/csv;charset=utf-8;'
  );
};

export const downloadARAgingPdf = ({ report, viewMode, asOfDate }) => {
  const PdfConstructor = typeof jsPDF === 'function' ? jsPDF : window.jspdf?.jsPDF;

  if (typeof PdfConstructor !== 'function') {
    throw new Error('PDF export is unavailable. Please refresh and try again.');
  }

  const pdf = new PdfConstructor({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 48;
  let y = 52;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text('Accounts Receivable Aging', margin, y);
  y += 20;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(90, 98, 92);
  pdf.text('Outstanding customer balances organized by aging period.', margin, y);
  pdf.text(`As of ${asOfDate}`, pageWidth - margin, y, { align: 'right' });
  y += 28;

  const rows = buildRows(report, viewMode);
  const columnWidth = (pageWidth - margin * 2) / rows[0].length;

  rows.forEach((row, rowIndex) => {
    if (y > 730) {
      pdf.addPage();
      y = 52;
    }

    if (rowIndex === 0 || rowIndex === rows.length - 1) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(245, 247, 246);
      pdf.rect(margin, y - 12, pageWidth - margin * 2, 20, 'F');
    } else {
      pdf.setFont('helvetica', 'normal');
    }

    pdf.setFontSize(8.5);
    pdf.setTextColor(35, 45, 38);

    row.forEach((value, columnIndex) => {
      const alignRight = columnIndex > 0;
      const x = alignRight
        ? margin + columnWidth * (columnIndex + 1) - 6
        : margin + columnWidth * columnIndex + 6;

      pdf.text(String(value), x, y, { align: alignRight ? 'right' : 'left' });
    });

    pdf.setDrawColor(225, 230, 226);
    pdf.line(margin, y + 6, pageWidth - margin, y + 6);
    y += 21;
  });

  pdf.save(`Accounts_Receivable_Aging_${asOfDate}.pdf`);
};